import { startCase } from 'lodash';
import { raiseToast } from '@fiverr-private/toast';
import { submitCheckoutV2 } from '../../../../utils/submitCheckoutV2';
import { translate } from '../../../../utils/translate';
import {
    MIXPANEL_PROVIDER_TYPE_MAP,
    MIXPANEL_SIGNING_MODE_TYPE_MAP,
} from '../../../../events/mixpanel/mixpanel.constants';
import { extractEmailDomain } from '../../../../utils/extractEmailDomain';
import { GrafanaEventName } from '../../../../events/grafana';
import {
    AUTHENTICATION_SIGN_IN_FLOW_TYPES,
    AuthenticationResponse,
    SIGNING_FLOW_TYPE_MAP,
} from '../../../../shared/constants';
import { useIdentificationContext } from '../../../IdentificationContext';
import { getAuthenticationFlowType } from '../../../../utils/getAuthenticationFlowType';
import { SocialProvider } from '../../../SocialButton/constants';
import { globalMixpanelReporter } from '../../../../events/mixpanel/mixpanel.reporter';
import { globalBigQueryReporter } from '../../../../events/bigQuery/bigQuery.reporter';
import { globalGrafanaReporter } from '../../../../events/grafana/grafana.reporter';

export interface SignupHandlers {
    onProfileSetup(): void;
    onSocialFallback(): void;
}

const useSocialSignInHandler = () => {
    const { finishSigningFlowAndNavigate, setIsSocialFlow, setMode, setRedirectUrl } = useIdentificationContext();

    const handler = (authenticationResponse: AuthenticationResponse) => {
        const { url: redirectUrl, guest_checkout: guestCheckoutData } = authenticationResponse;

        if (submitCheckoutV2('social', guestCheckoutData)) {
            return;
        }

        setIsSocialFlow(true);
        setMode('sign_in');
        setRedirectUrl(redirectUrl);

        finishSigningFlowAndNavigate();
    };

    return handler;
};

const useSocialSignupHandler = ({ onProfileSetup, onSocialFallback }: SignupHandlers) => {
    const { setEmail, setIsSocialFlow, setSkipActivation, setMode } = useIdentificationContext();
    const handler = (authenticationResponse: AuthenticationResponse) => {
        const { result_hash: result } = authenticationResponse;

        if (!result) {
            return;
        }

        const { user_data: userData = {} } = result;

        if (!userData?.suggested_email) {
            onSocialFallback();
            return;
        }

        const { suggested_email: email } = userData;

        setEmail(email);
        setIsSocialFlow(true);
        setSkipActivation(true);
        setMode('sign_up');

        onProfileSetup();
    };

    return handler;
};

export const useSocialSigningHandlers = ({ onProfileSetup, onSocialFallback }: SignupHandlers) => {
    const handleSocialSignup = useSocialSignupHandler({ onProfileSetup, onSocialFallback });
    const handleSocialSignIn = useSocialSignInHandler();

    const onSuccess = (provider: SocialProvider, authenticationResponse: AuthenticationResponse) => {
        const { result_hash: result } = authenticationResponse;
        const { flow_type: flowType } = result;
        const authenticationFlowType = getAuthenticationFlowType(flowType);

        const emailDomain = extractEmailDomain(result.user_data?.suggested_email);

        if (!authenticationFlowType) {
            return;
        }

        const eventName: Extract<
            GrafanaEventName,
            | 'social_signing.%{platform}.google_authentication_success'
            | 'social_signing.%{platform}.facebook_authentication_success'
            | 'social_signing.%{platform}.apple_authentication_success'
        > = `social_signing.%{platform}.${provider}_authentication_success`;

        globalGrafanaReporter.count(eventName);

        if (AUTHENTICATION_SIGN_IN_FLOW_TYPES.includes(authenticationFlowType)) {
            globalMixpanelReporter.enrichContext({
                provider: MIXPANEL_PROVIDER_TYPE_MAP[provider],
                mode: MIXPANEL_SIGNING_MODE_TYPE_MAP.sign_in,
                emailDomain,
                flow: 'Social',
            });
            globalBigQueryReporter.enrichContext({
                provider,
                mode: 'sign_in',
                emailDomain,
                flow: SIGNING_FLOW_TYPE_MAP.social,
            });
            globalMixpanelReporter.send('Identification - Authentication - Succeeded');

            handleSocialSignIn(authenticationResponse);
            return;
        }

        globalMixpanelReporter.enrichContext({
            provider: MIXPANEL_PROVIDER_TYPE_MAP[provider],
            mode: MIXPANEL_SIGNING_MODE_TYPE_MAP.sign_up,
            emailDomain,
            flow: 'Social Sign Up',
        });
        globalBigQueryReporter.enrichContext({
            provider,
            mode: 'sign_up',
            emailDomain,
            flow: SIGNING_FLOW_TYPE_MAP.socialSignUp,
        });

        handleSocialSignup(authenticationResponse);
    };

    const onError = (provider: SocialProvider) => {
        const eventName: Extract<
            GrafanaEventName,
            | 'social_signing.%{platform}.google_authentication_fail'
            | 'social_signing.%{platform}.facebook_authentication_fail'
            | 'social_signing.%{platform}.apple_authentication_fail'
        > = `social_signing.%{platform}.${provider}_authentication_fail`;

        globalGrafanaReporter.count(eventName);
        const providerName = startCase(provider);

        raiseToast({
            type: 'error',
            location: 'top',
            message: translate('social.errors.authentication', {
                params: {
                    provider: providerName,
                },
            }),
        });
    };

    return {
        onSuccess,
        onError,
    };
};
